import {
  GeoJSONSource,
  type Map as MaplibreMap,
  SourceSpecification
} from "maplibre-gl";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  MultiPolygon,
  Point,
  Polygon
} from "geojson";

import { COLOR_KEYS } from "@/constants/colors";
import { UNIQUE_ID_PROPERTY } from "@/constants/map";
import { Dataset, DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";
import { getClampedColor } from "@/utils/color";

import pointOnFeature from "@turf/point-on-feature";

import { generateColorRangesUniqueStops } from "../common";
import {
  changeColorCustomCategoryLayer,
  changeColorCustomRangeLayer,
  type LayerSublayers
} from "../map";
import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";
import { buildStepColorExpression } from "./stepExpression";

/**
 * Builds and registers the centroid points, then returns the collection so
 * callers can reuse it without the expensive `source.getData()` round-trip.
 */
export const addCentroidSource = (
  map: MaplibreMap | null,
  centroidSourceId: string,
  polygonData: FeatureCollection<Geometry>
): FeatureCollection<Point> | null => {
  if (!map) return null;

  // Build centroid points in one pass, avoiding multiple full-collection copies.
  // The previous approach used ~140 MB for an 11k-row boundary dataset.
  const centroidFeatures: Feature<Point>[] = [];

  polygonData.features.forEach((feature, index) => {
    const { geometry } = feature;
    if (geometry?.type !== "Polygon" && geometry?.type !== "MultiPolygon") {
      return;
    }

    const { coordinates } = geometry as Polygon | MultiPolygon;
    const hasCoords =
      Array.isArray(coordinates) &&
      coordinates.length > 0 &&
      (geometry.type === "Polygon"
        ? coordinates[0]?.length > 0
        : coordinates.some((polygon) => polygon[0]?.length > 0));

    if (!hasCoords) return;

    try {
      const centroid = pointOnFeature(
        feature as Feature<Polygon | MultiPolygon>
      );
      if (!centroid?.geometry) return;

      centroid.properties = {
        ...feature.properties,
        [UNIQUE_ID_PROPERTY]: feature.properties?.[UNIQUE_ID_PROPERTY] || index
      };
      centroidFeatures.push(centroid);
    } catch (e) {
      console.error(
        "pointOnFeature failed",
        feature.properties?.[UNIQUE_ID_PROPERTY],
        e
      );
    }
  });

  const centroidCollection: FeatureCollection<Point> = {
    type: "FeatureCollection",
    features: centroidFeatures
  };

  const sourceSpec: SourceSpecification = {
    type: "geojson",
    data: centroidCollection
  };

  if (map.getSource(centroidSourceId)) {
    const source = map.getSource<GeoJSONSource>(centroidSourceId);
    source?.setData(centroidCollection);
  } else {
    map.addSource(centroidSourceId, sourceSpec);
  }

  return centroidCollection;
};

export const addPolygonCatchmentSource = (
  map: MaplibreMap | null,
  datasetId: string,
  polygonData: FeatureCollection<Geometry> | Feature<Polygon, GeoJsonProperties>
): void => {
  if (!map) return;

  const withUniqueId = <G extends Geometry>(
    feature: Feature<G, GeoJsonProperties>,
    index: number
  ): Feature<G, GeoJsonProperties> => ({
    ...feature,
    properties: {
      ...feature.properties,
      [UNIQUE_ID_PROPERTY]: feature.properties?.[UNIQUE_ID_PROPERTY] ?? index
    }
  });

  const parsedPolygonData =
    polygonData.type === "FeatureCollection"
      ? {
          ...polygonData,
          features: polygonData.features.map((feature, index) =>
            withUniqueId(feature, index)
          )
        }
      : withUniqueId(polygonData, 0);

  map.addSource(datasetId, {
    type: "geojson",
    data: parsedPolygonData,
    promoteId: UNIQUE_ID_PROPERTY
  });
};

export const addPolygonSource = (
  map: MaplibreMap | null,
  datasetId: string,
  polygonData: FeatureCollection<Geometry>
): void => {
  if (!map) return;

  const parsedPolygonData: FeatureCollection<Geometry> = {
    ...polygonData,
    features: polygonData.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        [UNIQUE_ID_PROPERTY]: feature.properties?.[UNIQUE_ID_PROPERTY] || index
      }
    }))
  };

  map.addSource(datasetId, {
    type: "geojson",
    data: parsedPolygonData,
    promoteId: UNIQUE_ID_PROPERTY
  });
};

export const removePolygonSource = (
  map: MaplibreMap | null,
  datasetId: string
): void => {
  if (!map) return;

  map.removeSource(datasetId);
};

export const removePolygonLayer = (
  map: MaplibreMap | null,
  datasetId: string
): void => {
  if (!map) return;

  map.removeLayer(datasetId);
};

export const addPolygonCategoryLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  layerData: Omit<FeatureDataConfig, "data"> & { id: string },
  style: Dataset["style"],
  category: string
): void => {
  if (!map || !layerData.sublayers) return;

  const colorRanges = style.colorRanges as { value: string; color: string }[];

  // Add fill layer
  map.addLayer({
    id: layerData.sublayers.fill,
    type: "fill",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "fill-color": [
        "case",
        ["has", category],
        colorRanges.length
          ? [
              "match",
              ["get", category],
              colorRanges[0]?.value ?? "",
              getClampedColor(style.colorFamily, COLOR_KEYS, 0),
              ...colorRanges
                .slice(1)
                .flatMap((range, index) => [
                  range.value ?? "",
                  getClampedColor(style.colorFamily, COLOR_KEYS, index + 1)
                ]),
              getClampedColor(
                style.colorFamily,
                COLOR_KEYS,
                colorRanges.length - 1
              )
            ]
          : "#74c476",
        "#74c476"
      ],

      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.8,
        style?.fillOpacity || 0.7
      ]
    }
  });

  // Base outline — hidden on selected so neighbor white edges don't overpaint selection
  map.addLayer({
    id: layerData.sublayers.outline,
    type: "line",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#ffffff",
      "line-width": 1,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        0,
        1
      ]
    }
  });

  // Selection outline — separate layer drawn on top; render order guarantees no neighbor overpaint
  map.addLayer({
    id: layerData.sublayers.outlineSelected,
    type: "line",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#FFCF01",
      "line-width": 3,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        1,
        0
      ]
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, layerData, style);
  addRankLabelLayer(isRankingLabelEnabled, map, layerData, style);
};

/**
 * Adds polygon layers with the "Simple" color scale — a single flat fill +
 * outline applied to every feature (no per-value classification). Shared by
 * polygon, administrative, h3 and geohash layers. Falls back to the legacy
 * `fillColor`/`color` when no `singleStyleConfig` is set.
 */
export const addPolygonSingleStyleLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  layerData: Omit<FeatureDataConfig, "data"> & { id: string },
  style: Dataset["style"]
): void => {
  if (!map || !layerData.sublayers) return;

  const fillColor = style.singleStyleConfig?.fillColor ?? style.fillColor;
  const outlineColor = style.singleStyleConfig?.outlineColor ?? style.color;

  // Flat fill for every feature.
  map.addLayer({
    id: layerData.sublayers.fill,
    type: "fill",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "fill-color": fillColor,
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.8,
        style?.fillOpacity || 0.7
      ]
    }
  });

  // Base outline takes the Simple outline color (hidden when selected so the
  // selection outline shows on top).
  map.addLayer({
    id: layerData.sublayers.outline,
    type: "line",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": outlineColor,
      "line-width": 1,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        0,
        1
      ]
    }
  });

  // Selection outline (yellow), drawn on top.
  map.addLayer({
    id: layerData.sublayers.outlineSelected,
    type: "line",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#FFCF01",
      "line-width": 3,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        1,
        0
      ]
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, layerData, style);
  addRankLabelLayer(isRankingLabelEnabled, map, layerData, style);
};

/**
 * Adds polygon layers with the "Custom Ordinal" color scale: the standard
 * fill/outline/selection structure, then a per-value fill + outline driven by
 * the committed config (a feature's colors follow the category its attribute
 * value is assigned to; unassigned features fall back to "Other"). Shared by
 * polygon, administrative, h3 and geohash layers. Keeps creation-time paint in
 * sync with the on-save repaint so the colors survive reloads/filtering.
 */
export const addPolygonCustomCategoryLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  layerData: Omit<FeatureDataConfig, "data"> & { id: string },
  style: Dataset["style"],
  category: string
): void => {
  if (!map || !layerData.sublayers) return;

  // Build the flat fill/outline/selection layers, then override fill + outline
  // with the per-value expressions from the config.
  addPolygonSingleStyleLayers(
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    layerData,
    style
  );

  if (style.customCategoryConfig) {
    changeColorCustomCategoryLayer(
      map,
      layerData.sublayers as LayerSublayers,
      style.customCategoryConfig,
      category,
      style.fillOpacity ?? 0.7
    );
  }
};

/**
 * Adds polygon layers with the "Custom Breaks" color scale: the standard
 * fill/outline/selection structure, then a per-range fill + outline driven by
 * the committed config (a feature's colors follow the numeric range its value
 * falls into; gaps/outside fall back to "Other"). Shared by polygon,
 * administrative, h3 and geohash layers.
 */
export const addPolygonCustomRangeLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  layerData: Omit<FeatureDataConfig, "data"> & { id: string },
  style: Dataset["style"],
  category: string
): void => {
  if (!map || !layerData.sublayers) return;

  addPolygonSingleStyleLayers(
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    layerData,
    style
  );

  if (style.customRangeConfig) {
    changeColorCustomRangeLayer(
      map,
      layerData.sublayers as LayerSublayers,
      style.customRangeConfig,
      category,
      style.fillOpacity ?? 0.7
    );
  }
};

export const addPolygonAutoRangeLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataset: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map || !dataset.sublayers) return;

  const uniqueRanges = generateColorRangesUniqueStops(style.colorRanges);

  const stepColorExpression = buildStepColorExpression(uniqueRanges, category);

  // Add fill layer
  map.addLayer({
    id: dataset.sublayers.fill,
    type: "fill",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "fill-color": [
        "case",
        /* 1️⃣ property missing entirely → white */
        ["!", ["has", category]],
        "#FFFFFF",
        /* 2️⃣ category is null → grey */
        ["==", ["get", category], null],
        "#CECECE",
        /* 3️⃣ property exists and is not null → use step scale or constant color */
        stepColorExpression
      ],
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.8,
        style?.fillOpacity || 0.7
      ],
      // Border color based on selected state
      "fill-outline-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        "#FFCF01", // border color when selected is true
        "#fff" // default border color when selected is false
      ]
    }
  });

  // Base outline — hidden on selected so neighbor white edges don't overpaint selection
  map.addLayer({
    id: dataset.sublayers.outline,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#ffffff",
      "line-width": 1,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        0,
        1
      ]
    }
  });

  // Selection outline — separate layer drawn on top; render order guarantees no neighbor overpaint
  map.addLayer({
    id: dataset.sublayers.outlineSelected,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#FFCF01",
      "line-width": 3,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        1,
        0
      ]
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataset, style);
  addRankLabelLayer(isRankingLabelEnabled, map, dataset, style);
};
